import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { Observable, forkJoin, of } from 'rxjs';
import { concatMap, map, switchMap, tap } from "rxjs/operators";
import { FileDownloaderService } from '../../common/file-downloader.service';
import { UploadUtilService } from '../../common/photo-uploader/upload-util.service';
import { Themes } from 'src/app/models/formations/domaine';
import { IChampPersoViewModel } from 'src/app/pages/champs-perso/models/champ-perso-models';
import { JwtUrlAdaptor } from '../../adaptors/jwt-url-adaptor';
import { ODataAdaptor } from '../../adaptors/odata-adaptor';
import { ModeleEvaluationModelSimple } from '../../models/evaluations/modele.evaluation.model.simple';
import { CategorieFormationModel } from "../../models/formations/categorie-formation-model";
import { CodeDiplome } from "../../models/formations/code-diplome";
import { FormFormationModel } from '../../models/formations/formFormation.model';
import { FormationForFormationCompose, FormationModel } from '../../models/formations/formation.model';
import { FormationProgrammeModel } from '../../models/formations/formationProgramme.model';
import { ValeurListeFixeModel } from '../../models/listes-fixes/valeur-liste-fixe.model';
import { SessionColonneChampAdditionnel } from '../../models/sessions/session-colonne-champ-additionnel.model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { FileConversionsUtil, base64string } from '../../utils/file-conversions-util';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';

@Injectable({
  providedIn: 'root',
})
export class FormationsService {
  private readonly baseUrlApi = this.configService.config.urlApi;;
  public test
  constructor(
    private oDataAdaptor: ODataAdaptor,
    private jwtUrlAdaptor: JwtUrlAdaptor,
    private client: HttpClient,
    private notificationToastService: NotificationToastService,
    private http: HttpClient,
    private notificationService: NotificationToastService,
    private configService: ConfigService,
    private localStorageTokenService: LocalStorageTokenService,
    private uploadUtilService: UploadUtilService,
    private fileDownloaderService: FileDownloaderService,
  ) {}

  addFormation(formation: FormationModel): Observable<number> {
    return this.http
      .put<number>(`${this.baseUrlApi}/api/f/formations/addFormation`, formation)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('La formation a bien été créée.'))));
  }

  addFormationWithProgramme(formation: FormFormationModel): Observable<number> {
    let id = null;
    return this.addFormation(formation.formation).pipe(
      concatMap(idFormation => {
        id = idFormation;
        const programme = formation.formationProgramme;
        programme.idFormation = idFormation;
        return this.addProgramme(programme);
      }),
      map(() => {
        return id;
      }),
    );
  }

  getCodesDiplomes(inclureElementVide: boolean = true): Observable<CodeDiplome[]> {
    return this.http.get<CodeDiplome[]>(`${this.baseUrlApi}/api/f/formations/getCodesDiplomes`)
      .pipe(map(liste => inclureElementVide ? [...liste, {id: null, code: null, libelle: null}]: liste));
  }

  updateFormation(formation: FormationModel): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrlApi}/api/f/formations/updateFormation`, formation)
      .pipe(tap(() => this.notificationService.push(new ToastSuccesModel('La formation a été modifiée.'))));
  }

  /** Ajoute ou remplace l'image (le back vide UrlImage et supprime l'ancien document). */
  definirImageFormation(idFormation: number, image: File, texteAlternatif: string): Observable<void> {
    return this.uploadUtilService
      .uploadFile(`${this.baseUrlApi}/api/f/Formations/DefinirImageFormation`, {
        File: image,
        IdFormation: idFormation,
        TexteAlternatif: texteAlternatif,
      })
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Image téléversée avec succès'))));
  }

  /** Retire l'image (GUID et texte alternatif) et supprime le document. */
  supprimerImageFormation(idFormation: number): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrlApi}/api/f/Formations/SupprimerImageFormation/${idFormation}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Image supprimée avec succès'))));
  }

  /** Image en base64, ou null si la formation n'a pas d'image (corps vide). */
  chargerImageFormation(idFormation: number): Observable<base64string | null> {
    return this.fileDownloaderService
      .fetchFile(`${this.baseUrlApi}/api/f/Formations/GetImageFormation/${idFormation}`, null)
      .pipe(switchMap((blob: Blob) => blob.size === 0 ? of(null) : FileConversionsUtil.imageBlobToBase64(blob)));
  }

  updateProgramme(programme: FormationProgrammeModel): Observable<void> {
    return this.http.post<void>(`${this.baseUrlApi}/api/f/formations/UpdateFormationProgramme`, programme);
  }

  getFormationWithProgramme(idFormation: number): Observable<FormFormationModel> {
    const formation = this.http.get<FormationModel>(`${this.baseUrlApi}/api/f/formations/getFormation/` + idFormation);
    const formationProgramme = this.http.get<FormationProgrammeModel>(
      `${this.baseUrlApi}/api/f/formations/getFormationProgramme/` + idFormation,
    );
    return forkJoin({formation, formationProgramme}).pipe(map(v => Object.assign(new FormFormationModel(), v)));
  }

  getFormationSansProgramme(idFormation: number): Observable<FormationModel> {
    return this.http.get<FormationModel>(`${this.baseUrlApi}/api/f/formations/getFormation/` + idFormation);
  }

  activerOuDesactiverFormation(formation: FormationModel, active: boolean) {
    return this.http
      .post<void>(`${this.baseUrlApi}/api/f/formations/updateActiveFormation`, {Id: formation.id, Active: active})
      .pipe(
        tap(() => (formation.active = active)),
        tap(() =>
          this.notificationToastService.push(
            new ToastSuccesModel(active ? `Formation activée` : `Formation désactivée`),
          ),
        ),
      );
  }

  loadAnneesReferentiel(idFormation: number, avecElementVide: boolean = true) : Observable<number[]>{
    const result =  this.http.get<number[]>(`${this.baseUrlApi}/api/f/ReferentielMatieres/getAnneesReferentiel/${idFormation}`);
    if (avecElementVide) {
     return result.pipe(map(annees => [...annees, null]))
    }
    return result;
  }

  chargerEtatsFormation(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeEtatsFormation/getValeurs`);
  }

  chargerFamillesFormation(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeDomainesFormation/getValeurs`);
  }

  chargerThemesByDomainFormation(idDomain: number): Observable<Themes[]> {
    return this.http.get<Themes[]>(`${this.baseUrlApi}/api/f/Themes/GetThemesForDomaine/${idDomain}`);
  }

  chargerModalitesFormation(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeModalitesFormation/getValeurs`);
  }

  chargerNiveauxDiplomesFormation(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(
      `${this.baseUrlApi}/api/f/ListeFixeNiveauxDiplomeFormation/getValeurs`,
    );
  }

  chargerTypeQualificationVisee(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(
      `${this.baseUrlApi}/api/f/ListeTypeQualificationVisee/getValeurs`,
    );
  }

  chargerCategoriesFormation(): Observable<CategorieFormationModel[]> {
    return this.http.get<CategorieFormationModel[]>(
      `${this.baseUrlApi}/api/f/formations/getallformationscategories`,
    );
  }

  public getFormationsSelectionUrl(estFormationComposee: boolean): string {
    return `${this.baseUrlApi}/api/f/FormationsSelectionView?estFormationComposee=${estFormationComposee}`;
  }

  buildDataManagerGetFormations(): DataManager {
    return new DataManager({
      url: `${this.baseUrlApi}/api/f/formationsview`,
      adaptor: this.oDataAdaptor
    });
  }

  private addProgramme(programme: FormationProgrammeModel): Observable<void> {
    return this.http.put<void>(`${this.baseUrlApi}/api/f/formations/AddFormationProgramme`, programme);
  }

  updateFormationWithProgramme(formModifiee: FormFormationModel): Observable<any> {
    return this.updateFormation(formModifiee.formation).pipe(
      concatMap(() => {
        return this.updateProgramme(formModifiee.formationProgramme);
      }),
    );
  }

  getFormationModelesEvaluations(formationId: number, target?: number) :Observable<ModeleEvaluationModelSimple[]>{
    let url = `${this.baseUrlApi}/api/f/formations/getFormationsModelesEvaluations/${formationId}`;
    if (target !== undefined) {
      url += `?target=${target}`;
    }
    return this.http.get<ModeleEvaluationModelSimple[]>(url);
  }

  getModelesEvaluation(idFormation: number, text: string, type: number[], target?: number):Observable<ModeleEvaluationModelSimple[]>{
    let path = `api/f/formationsModelesEvaluationView/getModelesEvaluations/${idFormation}?$orderby=Titre`;
    if (target !== undefined) {
      path += `&Target=${target}`;
    }
    const searchText = text ? `contains(tolower(cast(Titre, 'Edm.String')), '${text}')`: null;
    const filterResult = type.length > 0 ? `Type in (${type})` : 'Type in ()';

    if(searchText){
      path += `&$filter=(${searchText} and ${filterResult})`;
    } else {
      path += `&$filter=(${filterResult})`;
    }

    return this.http.get<ModeleEvaluationModelSimple[]>(`${this.baseUrlApi}/${path}`);
  }

  updateFormationModelesEvaluations(idFormation: number, allModeles: ModeleEvaluationModelSimple[], messageSuccess: string, target: number): Observable<any> {
    return this.http.post(`${this.baseUrlApi}/api/f/formations/updateFormationModelesEvaluation?target=${target}`, {idFormation : idFormation, modelesEvaluations: allModeles})
      .pipe(tap(() => this.notificationService.push(new ToastSuccesModel(messageSuccess))));
  }

  upgradeVersionModeleEvaluationForFormation(idFormation: number, idModele: number): Observable<number> {
    return this.http.post<number>(`${this.baseUrlApi}/api/f/formations/UpgradeModeleEvaluationForFormation`, {idFormation, idModele})
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Le modèle a été mis à jour en dernière version.'))))
  }

  public getStates(): Observable<ValeurListeFixeModel[]>{
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeEtatsFormation/getValeurs`)
  }

  public getSessionsStates(): Observable<ValeurListeFixeModel[]>{
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeEtatsSession/getValeurs`)
  }

  public getColonnesChampsAdditionnelsSession(): Observable<SessionColonneChampAdditionnel[]> {
    return this.http.get<Record<string, unknown>[]>(
      `${this.baseUrlApi}/api/f/Sessions/GetColonnesChampsAddtionnelsSession`,
    ).pipe(
      map(colonnes => (colonnes ?? []).map(raw => ({
        guid: String(raw['guid'] ?? raw['Guid'] ?? ''),
        nom: String(raw['nom'] ?? raw['Nom'] ?? ''),
        type: Number(raw['type'] ?? raw['Type'] ?? 0),
        values: (raw['values'] ?? raw['Values'] ?? null) as string[] | null,
        cle: (raw['cle'] ?? raw['Cle'] ?? null) as string | null,
      }))),
    );
  }

  public getChampsAdditionnels(): Observable<IChampPersoViewModel[]>{
    return this.client.get<IChampPersoViewModel[]>(`${this.baseUrlApi}/api/f/ChampsPersoFormation/GetChamps`)
  }

  public getColonnesChampsAdditionnelsFormation(): Observable<SessionColonneChampAdditionnel[]> {
    return this.http.get<Record<string, unknown>[]>(
      `${this.baseUrlApi}/api/f/Formations/GetColonnesChampsAdditionnelsFormation`,
    ).pipe(
      map(colonnes => (colonnes ?? []).map(raw => ({
        guid: String(raw['guid'] ?? raw['Guid'] ?? ''),
        nom: String(raw['nom'] ?? raw['Nom'] ?? ''),
        type: Number(raw['type'] ?? raw['Type'] ?? 0),
        values: (raw['values'] ?? raw['Values'] ?? null) as string[] | null,
        cle: (raw['cle'] ?? raw['Cle'] ?? null) as string | null,
      }))),
    );
  }

  public getFormationsSessionsForParcours(idParcours: number, params: number): Observable<FormationForFormationCompose[]> {
    return this.client.get<FormationForFormationCompose[]>(`${this.baseUrlApi}/api/f/SessionsForParcoursView/GetSelection/${idParcours}?progression=${params}`)
  }

  public searchSessionForParcours(idParcours: number, progression: number, reference?: string | null): Observable<FormationForFormationCompose[]> {
    return this.client.get<FormationForFormationCompose[]>(`${this.baseUrlApi}/api/f/SessionsForParcoursView/GetSelection/${idParcours}?reference=${reference}&progression=${progression}`);
  }

  public duplicateFormation(formation: FormationModel): Observable<FormationModel>{
    return this.http.put<FormationModel>(`${this.baseUrlApi}/api/f/formations/DuplicateFormation/${formation.id}`, formation);
  }
}
